import React, { useEffect, useState, useCallback } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import Select from 'react-select';
import clsx from 'clsx';
import { AdultTypes } from '../../types/AdultTypes';
import { ChildrenTypes } from '../../types/ChildrenTypes';
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';
import CurrencyInputRef from '../../../../shared/components/form/CurrencyInputRef';
import { IconArrowRight, IconRefresh } from '@tabler/icons-react';
import { Spinner } from 'react-bootstrap';

// PERBAIKAN: Hapus `rateData` dari props
interface OccupancyPropsV4 {
    index: number;
    control: Control<CreateBookingFormV4>;
    errors: FieldErrors<CreateBookingFormV4>;
    isRateSuccess: boolean;
    isRateFetching: boolean;
    onUpdateTotal: (newTotal: number) => void;
    currentAccommodationTotal: number;
    revertPricePoint: number;
}

const OccupancyAndTotalV4: React.FC<OccupancyPropsV4> = ({ index, control, errors, isRateSuccess, isRateFetching, onUpdateTotal, currentAccommodationTotal, revertPricePoint }) => {
    // PERBAIKAN: Hapus `useMemo` yang menyebabkan masalah.
    // `currentAccommodationTotal` sekarang adalah "sumber kebenaran" otomatis.

    const [overrideTotal, setOverrideTotal] = useState<number | null>(null);

    // Efek untuk me-reset override manual setiap kali total dari induk berubah
    useEffect(() => {
        setOverrideTotal(null);
    }, [currentAccommodationTotal]);

    const displayTotal = overrideTotal ?? currentAccommodationTotal;

    const handleApplyTotal = () => {
        if (overrideTotal === null) return;
        onUpdateTotal(overrideTotal);
    };

    const handleResetTotal = useCallback(() => {
        setOverrideTotal(null);
        onUpdateTotal(revertPricePoint);
    }, [revertPricePoint, onUpdateTotal]);

    // Tombol apply dinonaktifkan jika nilai manual sama dengan nilai otomatis
    const isResetDisabled = currentAccommodationTotal === revertPricePoint;
    const isApplyDisabled = overrideTotal === null || displayTotal === currentAccommodationTotal;

    return (
        <div className="row mb-3 align-items-center">
            <div className="col-sm-4 row">
                <div className="col-6">
                    <label className="col-form-label">Adults:</label>
                    <Controller
                        control={control}
                        name={`bookingroom.${index}.adults`}
                        render={({ field }) => (
                            <Select
                                className={clsx({ 'is-invalid': errors.bookingroom?.[index]?.adults })}
                                options={AdultTypes}
                                value={AdultTypes.find(opt => Number(opt.value) === field.value)}
                                onChange={opt => field.onChange(Number(opt?.value ?? 1))}
                            />
                        )}
                    />
                </div>
                <div className="col-6">
                    <label className="col-form-label">Children:</label>
                    <Controller
                        control={control}
                        name={`bookingroom.${index}.children`}
                        render={({ field }) => (
                            <Select
                                className={clsx({ 'is-invalid': errors.bookingroom?.[index]?.children })}
                                options={ChildrenTypes}
                                value={ChildrenTypes.find(opt => Number(opt.value) === field.value)}
                                onChange={opt => field.onChange(Number(opt?.value ?? 0))}
                            />
                        )}
                    />
                </div>
            </div>
            {/* Kondisi `isRateSuccess` tetap relevan untuk tahu kapan harus menampilkan section ini */}
            {isRateSuccess && !isRateFetching && (
                <div className="col-sm-4">
                    <div className="col-12">
                        <label className="col-form-label">Total Accommodation:</label>

                        {/* BARU: Logika render kondisional yang lebih baik */}
                        {isRateFetching ? (
                            <div className="form-control bg-light d-flex align-items-center justify-content-center" style={{ height: '38px' }}>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2 small">Calculating...</span>
                            </div>
                        ) : isRateSuccess ? (
                            <div className="input-group">
                                <CurrencyInputRef
                                    name={`manual_total_${index}`}
                                    value={displayTotal}
                                    onChange={(_e, rawValue) => setOverrideTotal(Number(rawValue))}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleResetTotal}
                                    title="Reset manual changes"
                                    disabled={isResetDisabled}
                                >
                                    <IconRefresh size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={handleApplyTotal}
                                    disabled={isApplyDisabled}
                                    title={isApplyDisabled ? "No changes to apply" : "Apply and distribute this total to daily rates"}
                                >
                                    <IconArrowRight size={16} /> Apply
                                </button>
                            </div>
                        ) : (
                            // Fallback jika rate plan belum dipilih atau terjadi error
                            <div className="form-control bg-light text-muted" style={{ height: '38px' }}>
                                -
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OccupancyAndTotalV4;